import dayjs from 'dayjs';
import { isQueuedJob, isRunningJob, parseJobDescription } from '@/components/myboxes/relabelInFilterJobUtils';

export function downloadIsFinished(download) {
  return download?.status === 'ready' || download?.ready === true;
}

export function cropDownloadStatusLabel(download, t) {
  const status = download?.status;
  if (downloadIsFinished(download)) {
    return t('downloads.status_ready');
  }
  if (status === 'failed') {
    return t('downloads.status_failed');
  }
  const job = download?.job;
  if (status === 'preparing' || (job && isRunningJob({ ...job, processed: job.processed || 0, deleted_at: null }))) {
    return t('downloads.status_preparing');
  }
  if (status === 'queued' || (job && isQueuedJob({ ...job, processed: job.processed || 0, deleted_at: null }))) {
    return t('downloads.status_queued');
  }
  return t('downloads.status_queued');
}

export function cropDownloadProgressText(download) {
  const prepared = download?.job?.description?.prepared
    ?? parseJobDescription(download?.job)?.prepared
    ?? null;
  const total = download?.total_count
    ?? download?.job?.description?.total_estimate
    ?? parseJobDescription(download?.job)?.total_estimate
    ?? null;
  if (total > 0 && prepared != null) {
    return `${prepared} / ${total}`;
  }
  const progress = download?.progress ?? download?.job?.progress ?? 0;
  if (progress > 0) {
    return `${progress}%`;
  }
  return '';
}

export function cropDownloadWaitEstimate(download, t) {
  if (!download || downloadIsFinished(download) || download.status === 'failed') {
    return '';
  }
  const job = download.job || {};
  const progress = download.progress || job.progress || 0;
  if (progress >= 100) {
    return '';
  }
  const claimedAt = job.claimed_at;
  if (!claimedAt || progress <= 0) {
    if (download.status === 'queued' || !claimedAt) {
      return t('downloads.wait_in_queue');
    }
    return t('downloads.wait_preparing');
  }
  const elapsedSec = Math.max(1, dayjs().diff(dayjs(claimedAt), 'second'));
  const remainingSec = Math.round((elapsedSec * (100 - progress)) / progress);
  if (remainingSec < 60) {
    return t('downloads.wait_seconds', { seconds: Math.max(remainingSec, 5) });
  }
  const minutes = Math.ceil(remainingSec / 60);
  return t('downloads.wait_minutes', { minutes });
}

export function mergeDownloadWithLiveJob(download, liveJobs) {
  if (!download) {
    return download;
  }
  // API terminal state wins. A stale live job (processed=0 still in the
  // store after the zip is ready) must not keep the row on "preparing".
  if (download.status === 'failed') {
    return download;
  }
  if (downloadIsFinished(download)) {
    return { ...download, status: 'ready' };
  }
  const jobId = download.job_id;
  if (jobId == null) {
    return download;
  }
  const live = (liveJobs || []).find((j) => j.id === jobId);
  if (!live) {
    return download;
  }
  const description = parseJobDescription(live);
  return {
    ...download,
    progress: live.progress ?? download.progress,
    status: live.processed === 1
      ? 'ready'
      : (live.claimed_at ? 'preparing' : 'queued'),
    job: {
      ...(download.job || {}),
      id: live.id,
      progress: live.progress,
      processed: live.processed,
      claimed_at: live.claimed_at,
      on_hold: live.on_hold,
      created_at: live.created_at,
      description,
    },
  };
}
